import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const LOGO = "/logo-badge.png";
const inputCls = "w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 text-[#2C3539] font-medium";

export default function ResetPassword() {
  usePageTitle("Reset Password");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data.ok) setDone(true);
      else setError(data.error || "This reset link is invalid or has expired.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#23282B] av-grid flex items-center justify-center px-5 py-10" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="text-white/70 hover:text-[#9CC4DB] inline-flex items-center gap-2 text-xs tracking-widest uppercase mb-6" data-testid="reset-back-login">
          <ArrowLeft size={14} /> Back to Login
        </Link>
        <div className="bg-white border-t-4 border-[#9CC4DB] p-8 md:p-10">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Amazing View Contracting" className="h-16 w-auto rounded-[4px]" />
            <div>
              <div className="font-display text-2xl uppercase tracking-wider text-[#23282B]">Reset Password</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F5F80] font-bold">Employee Portal</div>
            </div>
          </div>

          {!token && (
            <div className="mt-8 bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700" data-testid="reset-no-token">
              This reset link is missing its token. Request a new one from the <Link to="/login" className="underline font-bold">login page</Link>.
            </div>
          )}

          {token && done && (
            <div className="mt-8" data-testid="reset-success">
              <div className="bg-green-50 border-l-4 border-green-600 px-4 py-4 text-sm text-green-800 inline-flex items-start gap-2">
                <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> Password updated. You can now sign in with your new password.
              </div>
              <Link to="/login" data-testid="reset-goto-login" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white font-display tracking-wider text-lg px-7 py-4 uppercase">
                Go to Sign In
              </Link>
            </div>
          )}

          {token && !done && (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
              <label className="block">
                <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">New Password (min 6)</span>
                <input data-testid="reset-password-input" type="password" required minLength={6} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
              </label>
              <label className="block">
                <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Confirm New Password</span>
                <input data-testid="reset-confirm-input" type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
              </label>
              {error && (
                <div data-testid="reset-error" className="bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700 inline-flex items-start gap-2">
                  <AlertCircle size={18} className="mt-0.5 shrink-0" /> {error}
                </div>
              )}
              <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white font-display tracking-wider text-lg px-7 py-4 uppercase disabled:opacity-50">
                <KeyRound size={18} /> {loading ? "Updating…" : "Set New Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
